import * as i1 from '@noda-lib/utils/stores';
import { RxStoreToken, provideRxChildStore } from '@noda-lib/utils/stores';
import * as i0 from '@angular/core';
import { InjectionToken, inject, Injectable, DestroyRef, Inject, DOCUMENT } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WINDOW } from '@noda-lib/utils/injection';
import { UseManagers, ExtendsFactory, State, ServiceManager, ServiceAction } from '@noda-lib/utils/managers';
import { fromEvent, map, filter, tap, take, throwError, firstValueFrom, Subject, Observable } from 'rxjs';
function toGTMOptions(userInfo) {
  return {
    uuid: userInfo.uuid,
    sID: userInfo.sessionId
  };
}
function toHttpHeaders(userInfo) {
  return {
    'X-Noda-CA-UUID': userInfo.uuid,
    'X-Noda-CA-SessionId': userInfo.sessionId
  };
}
class CrossAnalyticsDataFlags {
  constructor() {
    this.isLoadingUserInfo = false;
    this.isInitializing = false;
  }
}
class CrossAnalyticsStore {
  constructor() {
    this.iframe = null;
    this.userInfo = null;
    this.application = null;
  }
}
const CROSS_ANALYTICS_RX_STORE_TOKEN = RxStoreToken('CROSS_ANALYTICS_RX_STORE_TOKEN');
const CROSS_ANALYTICS_DEBUG_TOKEN = new InjectionToken('CROSS_ANALYTICS_DEBUG_TOKEN');
const CROSS_ANALYTICS_INITIALIZE_HANDLER = new InjectionToken('CROSS_ANALYTICS_INITIALIZE_HANDLER');
let CrossAnalyticsLoggerService = /*#__PURE__*/(() => {
  class CrossAnalyticsLoggerService {
    constructor() {
      this.withDebug = inject(CROSS_ANALYTICS_DEBUG_TOKEN, {
        optional: true
      });
      this.log = (...args) => {
        if (this.withDebug) {
          // eslint-disable-next-line no-console
          console.log('Cross Analytics Lib:', ...args);
        }
      };
      this.error = (...args) => {
        console.error('Cross Analytics Lib:', ...args);
      };
      this.warn = (...args) => {
        if (this.withDebug) {
          console.warn('Cross Analytics Lib:', ...args);
        }
      };
    }
    static {
      this.ɵfac = function CrossAnalyticsLoggerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CrossAnalyticsLoggerService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CrossAnalyticsLoggerService,
        factory: CrossAnalyticsLoggerService.ɵfac
      });
    }
  }
  return CrossAnalyticsLoggerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let IframeClientService = /*#__PURE__*/(() => {
  class IframeClientService {
    constructor(storeService) {
      this.storeService = storeService;
      this.destroyRef = inject(DestroyRef);
    }
    postMessage(message) {
      const stream$ = fromEvent(window, 'message').pipe(map(event => event.data), filter(data => {
        return data.timestamp === message.timestamp && data.type === message.type;
      }), tap(data => {
        if (data.status !== 200) {
          throw new Error(data.errorMessage || 'Something went wrong');
        }
      }), take(1), takeUntilDestroyed(this.destroyRef));
      const iframe = this.storeService.getValue('iframe');
      if (!iframe) {
        return throwError(() => new Error('Library is not initialized'));
      }
      if (!iframe.isConnected) {
        return throwError(() => new Error('Iframe is not connected'));
      }
      iframe.contentWindow?.postMessage(message, '*');
      return stream$;
    }
    static {
      this.ɵfac = function IframeClientService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || IframeClientService)(i0.ɵɵinject(CROSS_ANALYTICS_RX_STORE_TOKEN));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: IframeClientService,
        factory: IframeClientService.ɵfac
      });
    }
  }
  return IframeClientService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const CrossAnalyticsDataManagers = UseManagers(['actions', 'detect', 'error', 'streams', 'unsubscribe']);
let CrossAnalyticsDataService = /*#__PURE__*/(() => {
  class CrossAnalyticsDataService extends ExtendsFactory(State({
    flags: CrossAnalyticsDataFlags
  }), ServiceManager(CrossAnalyticsDataManagers)) {
    constructor(storeService) {
      super();
      this.storeService = storeService;
      this.logger = inject(CrossAnalyticsLoggerService);
      this.iframeClient = inject(IframeClientService);
      this.destroyRef = inject(DestroyRef);
      this.window = inject(WINDOW);
      this.document = inject(DOCUMENT);
    }
    ngOnDestroy() {
      this.unsubscribe();
    }
    initIframe(env) {
      if (!env.APP_CROSS_ANALYTICS_HOST) {
        this.logger.error('Cross analytics host is not defined');
        return;
      }
      this.setState('flags', 'isInitializing', true);
      const iframe = this.document.createElement('iframe');
      iframe.src = env.APP_CROSS_ANALYTICS_HOST;
      iframe.hidden = true;
      // TODO: check iframe loading
      const next = response => {
        this.action(new ServiceAction('init-iframe', iframe));
        this.action(new ServiceAction('user-info', response));
        this.setState('flags', 'isInitializing', false);
      };
      const error = e => {
        this.setState('flags', 'isInitializing', false);
        this.detectError('error-init-iframe')(e);
      };
      fromEvent(this.window, 'message').pipe(map(event => event.data), filter(data => data?.type === 'USER_INFO'), map(data => data.payload), take(1), takeUntilDestroyed(this.destroyRef)).subscribe({
        next,
        error
      });
      this.document.body.appendChild(iframe);
    }
    async post(payload, timestamp, url) {
      const currentUrl = url || this.window.location.href;
      await firstValueFrom(this.storeService.selectFirstDefinedValue('iframe'));
      const event = {
        ...payload,
        customData: Object.assign({}, payload.customData, {
          navigator: this.navigatorData
        }),
        userId: this.storeService.getValue('userInfo')?.uuid,
        sessionId: this.storeService.getValue('userInfo')?.sessionId,
        url: currentUrl,
        application: this.storeService.getValue('application')
      };
      this.postRequest(event, timestamp);
    }
    async postRequest(event, timestamp) {
      const next = response => {
        this.action(new ServiceAction('post', response));
      };
      this.iframeClient.postMessage({
        type: 'SEND_REQUEST',
        payload: event,
        timestamp
      }).subscribe({
        next,
        error: e => this.detectError('error-post')(e)
      });
    }
    get navigatorData() {
      return {
        userAgent: this.window.navigator.userAgent,
        userAgentData: {
          brands: this.window.navigator.userAgentData?.brands,
          mobile: this.window.navigator.userAgentData?.mobile,
          platform: this.window.navigator.userAgentData?.platform
        },
        platform: this.window.navigator.platform,
        appCodeName: this.window.navigator.appCodeName,
        appName: this.window.navigator.appName,
        appVersion: this.window.navigator.appVersion,
        language: this.window.navigator.language,
        languages: this.window.navigator.languages,
        cookieEnabled: this.window.navigator.cookieEnabled
      };
    }
    sendGAEvent(userInfo) {
      if (!this.window.dataLayer) {
        return;
      }
      const event = {
        eventCategory: 'User',
        eventActions: 'userEvent',
        event: 'ndb_ca_connect',
        options: JSON.stringify({
          uuid: userInfo.uuid,
          sID: userInfo.sessionId
        })
      };
      this.window.dataLayer.push(event);
    }
    static {
      this.ɵfac = function CrossAnalyticsDataService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CrossAnalyticsDataService)(i0.ɵɵinject(CROSS_ANALYTICS_RX_STORE_TOKEN));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CrossAnalyticsDataService,
        factory: CrossAnalyticsDataService.ɵfac
      });
    }
  }
  return CrossAnalyticsDataService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let CrossAnalyticsViewService = /*#__PURE__*/(() => {
  class CrossAnalyticsViewService {
    constructor(storeService) {
      this.storeService = storeService;
      this.dataService = inject(CrossAnalyticsDataService);
      this.destroyRef = inject(DestroyRef);
      this.logger = inject(CrossAnalyticsLoggerService);
      this.initializeHandler = inject(CROSS_ANALYTICS_INITIALIZE_HANDLER, {
        optional: true
      });
      this.observers = new Subject();
    }
    initActionsListener() {
      this.dataService.listenActions().pipe(tap(this.logger.log), takeUntilDestroyed(this.destroyRef)).subscribe(({
        type,
        payload
      }) => {
        switch (type) {
          case 'init-iframe':
            this.afterInitIframe(payload);
            break;
          case 'user-info':
            this.afterGetUserInfo(payload);
            break;
          case 'post':
            this.afterPost(payload);
        }
      });
    }
    initErrorsListener() {
      this.dataService.listenErrors().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(error => {
        this.logger.error(error.error.message);
      });
    }
    initIframe(env) {
      this.dataService.initIframe(env);
    }
    post(payload, url) {
      const timestamp = Date.now();
      const obs = new Observable(observer => {
        this.observers.pipe(takeUntilDestroyed(this.destroyRef), filter(value => value === timestamp), take(1)).subscribe(() => {
          observer.next();
          observer.complete();
        });
      });
      this.dataService.post(payload, timestamp, url);
      return obs;
    }
    afterInitIframe(payload) {
      this.storeService.setValue('iframe', payload);
    }
    afterGetUserInfo(payload) {
      this.storeService.setValue('userInfo', payload);
      this.dataService.sendGAEvent(payload);
      if (this.initializeHandler) {
        this.initializeHandler(payload);
      }
    }
    afterPost(payload) {
      this.logger.log('Succesfully send data:', payload);
      this.observers.next(payload.timestamp);
    }
    static {
      this.ɵfac = function CrossAnalyticsViewService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CrossAnalyticsViewService)(i0.ɵɵinject(CROSS_ANALYTICS_RX_STORE_TOKEN));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CrossAnalyticsViewService,
        factory: CrossAnalyticsViewService.ɵfac
      });
    }
  }
  return CrossAnalyticsViewService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let CrossAnalyticsService = /*#__PURE__*/(() => {
  class CrossAnalyticsService {
    constructor(storeService) {
      this.storeService = storeService;
      this.viewService = inject(CrossAnalyticsViewService);
      this.destroyRef = inject(DestroyRef);
      this.logger = inject(CrossAnalyticsLoggerService);
    }
    init(application, env) {
      if (this.storeService.getValue('iframe')) {
        this.logger.warn('Cross analytics is already initialized');
        return;
      }
      this.storeService.setValue('application', application);
      this.viewService.initIframe(env);
      this.viewService.initActionsListener();
      this.viewService.initErrorsListener();
    }
    selectUserInfo() {
      return this.storeService.selectFirstDefinedValue('userInfo').pipe(takeUntilDestroyed(this.destroyRef));
    }
    getUserInfo() {
      return this.storeService.getValue('userInfo');
    }
    post(payload, url) {
      return this.viewService.post(payload, url);
    }
    static {
      this.ɵfac = function CrossAnalyticsService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CrossAnalyticsService)(i0.ɵɵinject(CROSS_ANALYTICS_RX_STORE_TOKEN));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CrossAnalyticsService,
        factory: CrossAnalyticsService.ɵfac
      });
    }
  }
  return CrossAnalyticsService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function provideCrossAnalytics(...features) {
  return Array.from(features).concat([provideRxChildStore(CROSS_ANALYTICS_RX_STORE_TOKEN, CrossAnalyticsStore), CrossAnalyticsService, CrossAnalyticsLoggerService, CrossAnalyticsDataService, CrossAnalyticsViewService, IframeClientService]);
}
function withCADebug() {
  return {
    provide: CROSS_ANALYTICS_DEBUG_TOKEN,
    useValue: true
  };
}
function withCAInitializeHandler(handler) {
  return {
    provide: CROSS_ANALYTICS_INITIALIZE_HANDLER,
    useFactory: handler
  };
}

/*
 * Public API Surface of lib
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CrossAnalyticsService, IframeClientService, provideCrossAnalytics, toGTMOptions, toHttpHeaders, withCADebug, withCAInitializeHandler };
